import { axiosAPI as axios, withLoading, getTranslateFunc } from 'utils';

import { SET_COMMON_DATA, UPDATE_CONFIG, CHANGE_LOCALE } from './types';
import API from 'api';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { cacheCommonRuntime, getCachedCommonRuntime } from 'mobile-modules/offline/cache';

const filter = 'common';
const MERGE_PLAY_LOCALE_STRINGS = 'MERGE_PLAY_LOCALE_STRINGS';

const isWebPlayRoute = () => !isMobileAppRuntime()
  && typeof window !== 'undefined'
  && /^\/play(?:\/|$)/.test(window.location.pathname);

// ALL browser routes require the web bootstrap: the app can start at a tenant
// landing URL, registration, deep link or post-login redirect, not just /admin.
// Gating the fast bootstrap by pathname silently restores the 5 MB GET /all.
// /play has its own smaller bootstrap; native iOS/Android keep runtime-assets.
const isWebAdminRoute = () => !isMobileAppRuntime()
  && typeof window !== 'undefined'
  && !isWebPlayRoute();

const canDecodeWebAdminRuntime = () => typeof window !== 'undefined'
  && typeof window.DecompressionStream === 'function'
  && typeof window.Blob === 'function'
  && typeof window.Response === 'function'
  && typeof window.atob === 'function';

const decodeWebAdminRuntime = async (payload) => {
  if (payload?.codec !== 'gzip-base64') return payload;
  if (!canDecodeWebAdminRuntime()) throw new Error('gzip decoder unavailable');
  const bytes = Uint8Array.from(window.atob(payload.data || ''), c => c.charCodeAt(0));
  const stream = new window.Blob([bytes]).stream()
    .pipeThrough(new window.DecompressionStream('gzip'));
  const json = await new window.Response(stream).text();
  return JSON.parse(json);
};

const safeWebPlayLocale = (value) => {
  const locale = String(value || 'en').trim().toLowerCase();
  return /^[a-z]{2,4}(?:[-_][a-z]{2,4})?$/.test(locale) ? locale : 'en';
};

// Called before web Play constructs the Solution translator; this prevents a
// short initial bootstrap from losing Spanish/Vietnamese/other UI translations.
export const ensureWebPlayLocaleStrings = (requestedCode) => async (dispatch, getState) => {
  if (!isWebPlayRoute()) return;
  const locale = safeWebPlayLocale(requestedCode);
  if ((getState().common?.playLoadedLocales || []).includes(locale)) return;
  const response = await axios.post('/api/common/all', {
    runtime: 'play', locale, stringsOnly: true,
  }, { timeout: 45000 });
  if (!Array.isArray(response.data?.strings)) throw new Error('Invalid Play locale response');
  dispatch({
    filter,
    type: MERGE_PLAY_LOCALE_STRINGS,
    payload: { locale, strings: response.data.strings },
  });
};

const pendingAdminLocaleRequests = new Map();

// Called by statusActions.changeLocale before changing the Admin UI language.
// It merges translated fields into existing strings instead of replacing
// products, regions, templates, or any data used by the editors.
export const ensureWebAdminLocaleStrings = (requestedCode) => async (dispatch, getState) => {
  const locale = safeWebPlayLocale(requestedCode);
  if (!(getState().common?.adminLoadedLocales)) return;
  if (getState().common.adminLoadedLocales.includes(locale)) return;
  if (!pendingAdminLocaleRequests.has(locale)) {
    const request = (async () => {
      const response = await axios.post('/api/common/all', {
        runtime: 'admin', locale, stringsOnly: true,
        ...(canDecodeWebAdminRuntime() ? { responseCodec: 'gzip-base64' } : {}),
      }, { timeout: 45000 });
      let data;
      try { data = await decodeWebAdminRuntime(response.data); } catch (error) {
        const fallback = await axios.post('/api/common/all', {
          runtime: 'admin', locale, stringsOnly: true,
        }, { timeout: 45000 });
        data = fallback.data;
      }
      if (!Array.isArray(data?.strings)) throw new Error('Invalid Admin locale response');
      dispatch({ filter, type: 'MERGE_ADMIN_LOCALE_STRINGS',
        payload: { locale, strings: data.strings } });
      // The global status.translate is a closure over the *old* strings array.
      // Rebind it to merged common.strings without changing the selected admin
      // UI language. Otherwise WordAG localeOverride still returns not found.
      const currentUiLocale = getState().status?.lang;
      if (currentUiLocale?.code) {
        dispatch({ filter: 'status', type: CHANGE_LOCALE,
          payload: { locale: currentUiLocale,
            translate: getTranslateFunc(getState().common.strings, currentUiLocale.code) } });
      }
    })();
    pendingAdminLocaleRequests.set(locale, request);
    request.then(() => pendingAdminLocaleRequests.delete(locale),
      () => pendingAdminLocaleRequests.delete(locale));
  }
  return pendingAdminLocaleRequests.get(locale);
};

const dispatchMobileLoadingPhase = (detail) => {
  if (!isMobileAppRuntime() || typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent('flc:mobile-loading-phase', {
    detail,
  }));
};

export const getCommonData = (forceRefresh = false) => (dispatch) =>
  withLoading(dispatch, async () => {
    const mobileRuntime = isMobileAppRuntime();

    if (mobileRuntime) {
      dispatchMobileLoadingPhase({
        active: true,
        label: 'Loading app data...',
        detail: 'Loading mobile runtime from server...',
      });
    }

    try {
      const webAdmin = isWebAdminRoute();
      const refreshQuery = forceRefresh
        ? `${isWebPlayRoute() || webAdmin ? '&' : '?'}refresh=1&_=${Date.now()}`
        : '';
      // Web routes (including unknown/deep-link paths) must never fall back to
      // GET /api/common/all and block on the global S3 catalog.
      const webPlay = isWebPlayRoute();
      const playLocale = (webPlay || webAdmin)
        ? safeWebPlayLocale(typeof localStorage !== 'undefined' && localStorage.getItem('localeCode'))
        : 'en';
      const endpoint = mobileRuntime
        ? '/api/mobile-common/runtime-assets'
        : '/api/common/all';
      if (!mobileRuntime && typeof window !== 'undefined') {
        console.info('[web-bootstrap] compact POST /api/common/all', {
          mode: webPlay ? 'play' : 'admin', path: window.location.pathname,
        });
      }
      let data;
      if (mobileRuntime && !forceRefresh) {
        const cached = await getCachedCommonRuntime();
        if (cached) {
          data = cached;
          // Startup must not wait on the large runtime-assets endpoint. Refresh
          // the cache in the background and use it on the next render/startup.
          axios.get(endpoint, { timeout: 45000 })
            .then((res) => cacheCommonRuntime(res.data))
            .catch((error) => console.info('[mobile-runtime] background refresh skipped', error?.message || error));
        }
      }
      try {
        if (!data) {
          const res = (webAdmin || webPlay)
            ? await axios.post(endpoint, {
              runtime: webAdmin ? 'admin' : 'play', locale: playLocale,
              ...(forceRefresh ? { refresh: true } : {}),
              ...(webAdmin && canDecodeWebAdminRuntime() ? { responseCodec: 'gzip-base64' } : {}),
            }, { timeout: 120000 })
            : await axios.get(`${endpoint}${refreshQuery}`, { timeout: mobileRuntime ? 45000 : 120000 });
          data = res.data;
          if (webAdmin) {
            try {
              data = await decodeWebAdminRuntime(data);
            } catch (decodeError) {
              console.warn('[web-admin-runtime] gzip decode failed; retrying JSON', decodeError);
              const fallback = await axios.post('/api/common/all', {
                runtime: 'admin', locale: playLocale,
              }, { timeout: 120000 });
              data = fallback.data;
            }
            if (!Array.isArray(data?.strings) || !Array.isArray(data?.locales)
              || !Array.isArray(data?.products) || !Array.isArray(data?.regions)
              || !Array.isArray(data?.learningCenters) || !Array.isArray(data?.activityTemplates)) {
              throw new Error('Invalid web Admin bootstrap');
            }
            data = { ...data, adminLoadedLocales: [...new Set(['en', playLocale])] };
          }
          if (webPlay) {
            if (!Array.isArray(data?.strings) || !Array.isArray(data?.locales)) {
              throw new Error('Invalid web Play bootstrap');
            }
            data = { ...data, playLoadedLocales: [...new Set(['en', playLocale])] };
          }
          if (mobileRuntime) await cacheCommonRuntime(data);
        }
      } catch (requestError) {
        if (webPlay) {
          console.error('[web-play-bootstrap] compact Lambda endpoint failed; no S3 fallback', requestError);
          throw requestError;
        } else if (webAdmin) {
          // Do not silently download 5 MB of all.json.gz when a Lambda handler
          // is missing or returns an error. Surface the deployment error so the
          // fast path cannot appear to succeed while actually taking 40 sec.
          console.error('[web-admin-bootstrap] compact Lambda endpoint failed; no S3 fallback', requestError);
          throw requestError;
        } else if (!mobileRuntime) {
          throw requestError;
        }
        if (mobileRuntime) data = await getCachedCommonRuntime();
        if (mobileRuntime) {
          if (!data) throw requestError;
          console.info('[mobile-offline] using cached runtime assets');
        }
      }

      dispatch({
        filter,
        type: SET_COMMON_DATA,
        payload: data,
      });

      if (mobileRuntime) {
        dispatchMobileLoadingPhase({
          active: true,
          label: 'Preparing app...',
          detail: 'Mobile assets loaded',
        });
      }

      return data;
    } catch (err) {
      console.log(err);
      return false;
    } finally {
      if (mobileRuntime) {
        dispatchMobileLoadingPhase({ active: false });
      }
    }
  });

export const updateConfig = (values) => async (dispatch) => {
  const result = await API.config.update(values);

  if (result.success) {
    dispatch({
      filter,
      type: UPDATE_CONFIG,
      payload: result.data,
    });
  }

  return result;
};

export const viewLoginPageDocument = (
  key,
  localeCode,
  documentId,
  openWindow = true,
  silent = false,
) => dispatch => {
  const request = async () => {
    try {
      const res = await axios.get(`/api/config/login-page-documents`, {
        params: {
          key,
          localeCode,
          documentId,
        },
      });
      const url = res.data;
      if (openWindow) window.open(url);
      return url;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  return silent ? request() : withLoading(dispatch, request);
};

export const uploadFiles = (data) => (dispatch) =>
  withLoading(dispatch, () => API.storage.uploadFiles(data));

export const uploadFile = (key, file) => (dispatch) =>
  withLoading(dispatch, () => API.storage.uploadFile(key, file));

export const deleteFiles = (keys) => (dispatch) =>
  withLoading(dispatch, () => API.storage.deleteFiles(keys));

export const getMasterOrganizationById = (id) => (dispatch) =>
  withLoading(dispatch, async () => {
    try {
      const res = await axios.get(
        `/api/common/get-master-organization-by-id/${id}`
      );
      return res.data;
    } catch (err) {
      console.log(err);
      return null;
    }
  });
